/**
 * useStudioExport — renders the current session into stems + a mixdown.
 *
 * Optimisations:
 *   1. Render VOCAL and INSTRUMENTAL stems in parallel via OfflineAudioContext,
 *      each bounded by the real useful duration so no samples are rendered
 *      past the audio.
 *   2. MIX in-JS by summing the two rendered buffers with the current
 *      volume/mute gains — skips the 3rd OfflineAudioContext render pass.
 *   3. ENCODE all stems to 320kbps MP3 in parallel Web Workers (~4x smaller
 *      uploads than WAV). When the instrumental is an untouched library
 *      riddim (no offset, no trim) we skip encoding/uploading it entirely
 *      and reference it by id instead.
 */
import { useCallback } from 'react';
import { SAMPLE_RATE, buildVocalFxChain, encodeMp3, mixDown } from './studioAudioUtils';

export function useStudioExport(core) {
  const { clipsRef, mutes, volumes, reverbRef, delayRef, getCtx } = core;

  const renderStems = useCallback(async (opts = {}) => {
    const { onPhase } = opts;
    const { instrumental: inst, vocal: voc } = clipsRef.current;
    const total = Math.max(
      inst ? inst.offset + inst.buffer.duration : 0,
      voc ? voc.offset + voc.buffer.duration : 0,
      0.1,
    );
    const rate = SAMPLE_RATE;
    const totalSamples = Math.ceil(total * rate);

    const renderStem = async (clip) => {
      if (!clip) return null;
      const off = new OfflineAudioContext(2, totalSamples, rate);
      const g = off.createGain();
      g.gain.value = 1;
      g.connect(off.destination);
      const src = off.createBufferSource();
      src.buffer = clip.buffer;
      src.connect(g);
      src.start(clip.offset, 0);
      return off.startRendering();
    };

    const [vocBuf, instBuf] = await Promise.all([
      renderStem(voc),
      renderStem(inst),
    ]);

    // FX are baked into the MIXDOWN only — the vocal stem stays dry so the
    // buyer's engineer always gets a clean acapella.
    const reverbAmt = reverbRef.current;
    const delayAmt = delayRef.current;
    let wetBuf = null;
    if (vocBuf && (reverbAmt > 0.005 || delayAmt > 0.005)) {
      const off = new OfflineAudioContext(2, totalSamples, rate);
      const src = off.createBufferSource();
      src.buffer = vocBuf;
      buildVocalFxChain(off, src, off.destination, reverbAmt, delayAmt);
      src.start(0);
      wetBuf = await off.startRendering();
    }

    const instGain = mutes.inst ? 0 : volumes.inst;
    const vocGain = mutes.vocal ? 0 : volumes.vocal;
    const mixBuf = mixDown(getCtx(), instBuf, vocBuf, instGain, vocGain, totalSamples, rate, wetBuf);

    // Untouched library instrumental → reuse the stored file instead of
    // re-encoding and re-uploading a byte-identical render.
    const reuseInstrumental = !!(inst?.id) && inst.offset === 0 && !inst.edited;

    onPhase?.('encoding');
    const [vocalBlob, mixdownBlob, instrumentalBlob] = await Promise.all([
      vocBuf ? encodeMp3(vocBuf) : null,
      encodeMp3(mixBuf),
      instBuf && !reuseInstrumental ? encodeMp3(instBuf) : null,
    ]);

    return {
      vocalBlob,
      instrumentalBlob,
      mixdownBlob,
      reusedInstrumentalId: reuseInstrumental ? inst.id : null,
    };
  }, [getCtx, mutes, volumes, clipsRef, reverbRef, delayRef]);

  return { renderStems };
}
